import { useAuth } from '@clerk/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from '#router'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { useStreak } from '../streak-context'
import { playPrimedSubmitSound } from '../user-profile/code-editor-settings'
import { useUserSettings } from '../user-settings-context'
import { AttemptsPane, useSessionRefresh } from './attempts-pane'
import { CodeEditorPane } from './code-pane'
import { DescriptionPane } from './description-pane'
import { EditorialPane } from './editorial-pane'
import { useExecutionOptional } from './execution-context'
import { useSubmissionSuccess } from './hooks/use-submission-success'
import { MobileProblemDrawer } from './mobile-problem-drawer'
import { NotesPane } from './notes-pane'
import { SessionLifecycleModals } from './session-lifecycle-modals'
import { TestCasesPane } from './test-cases-pane'
import { TestResultsPane } from './test-results-pane'

type Tab = 'description' | 'editorial' | 'notes' | 'attempts'

const TABS: { id: Tab; label: string }[] = [
  { id: 'description', label: 'Description' },
  { id: 'editorial', label: 'Editorial' },
  { id: 'notes', label: 'Notes' },
  { id: 'attempts', label: 'Attempts' },
]

export function MobileEditor() {
  const location = useLocation()
  const { isSignedIn } = useAuth()
  const problemSlug = location.searchParams.get('problem')
  const listId = location.searchParams.get('list')
  const utils = trpc.useUtils()

  const [activeTab, setActiveTab] = useState<Tab>('description')
  const [problemId, setProblemId] = useState<string | null>(null)
  const [autoExpandExecutionId, setAutoExpandExecutionId] = useState<
    string | null
  >(null)

  const testResultsRef = useRef<HTMLDivElement>(null)
  const execution = useExecutionOptional()
  const sessionRefresh = useSessionRefresh()
  const streakContext = useStreak()
  const streakCheck = trpc.streak.check.useMutation()
  const { settings } = useUserSettings()

  // Refs for values used in the submission callback — keeps useCallback stable
  // while always seeing the latest mutation/context objects.
  const streakCheckRef = useRef(streakCheck)
  streakCheckRef.current = streakCheck
  const streakContextRef = useRef(streakContext)
  streakContextRef.current = streakContext
  const utilsRef = useRef(utils)
  utilsRef.current = utils

  // Fetch problem ID (same pattern as desktop editor)
  useEffect(() => {
    setProblemId(null)
    if (!problemSlug) return

    // Race guard: if problemSlug changes again before this resolve settles,
    // the cleanup below flips `ignore` so a stale, slower response can't
    // overwrite problemId with the previous problem's id.
    let ignore = false

    const fetchProblemId = async () => {
      try {
        const data = await utils.problems.resolveSlug.fetch({
          slug: problemSlug,
        })
        if (!ignore) setProblemId(data.problemId)
      } catch (error) {
        if (!ignore) console.error('Failed to fetch problem ID:', error)
      }
    }

    fetchProblemId()

    return () => {
      ignore = true
    }
  }, [problemSlug, utils])

  // Auto-scroll to test results when execution starts
  useEffect(() => {
    if (execution?.isExecuting && activeTab === 'description') {
      testResultsRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [execution?.isExecuting, activeTab])

  // Auto-switch to attempts tab on successful submission
  useSubmissionSuccess({
    executionResult: execution?.result ?? null,
    executionMode: execution?.executionMode ?? null,
    isExecuting: execution?.isExecuting ?? false,
    onSuccess: useCallback(
      (executionId: string | null) => {
        // Play success sound (primed during submit button tap for iOS compatibility)
        playPrimedSubmitSound(settings.editor.successSubmitSound)

        if (!sessionRefresh) return

        // Refresh attempts data, then switch tabs and expand the new session
        sessionRefresh()
          .then(() => {
            setActiveTab('attempts')
            setAutoExpandExecutionId(executionId)
          })
          .catch((error) => {
            console.error('Failed to refresh attempts:', error)
          })

        // Check streak (fire-and-forget, doesn't block the UI)
        if (problemId) {
          streakCheckRef.current.mutate(
            { problemId },
            {
              onSuccess: (result) => {
                utilsRef.current.streak.get.invalidate()
                if (result.earned) {
                  streakContextRef.current.triggerStreakEarned(
                    result.currentStreak,
                  )
                }
              },
            },
          )
        }
      },
      [sessionRefresh, problemId, settings.editor.successSubmitSound],
    ),
  })

  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{ height: 'calc(100dvh - 4rem - 1px)' }}
    >
      <SessionLifecycleModals
        onSwitchToAttempts={() => setActiveTab('attempts')}
      />
      {/* Tab bar with problem info */}
      <div className="flex shrink-0 items-center border-b border-[var(--editor-border)] bg-[var(--editor-tab)]">
        <div
          className="flex min-w-0 flex-1 overflow-hidden"
          role="tablist"
          aria-label="Editor tabs"
        >
          {TABS.map((tab) => (
            <button
              type="button"
              key={tab.id}
              id={`tab-${tab.id}`}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              className={cn(
                'min-w-0 shrink px-1.5 py-3 text-sm font-medium transition-colors min-h-[44px] truncate sm:px-5 sm:shrink-0',
                activeTab === tab.id
                  ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-0.5 border-l border-[var(--editor-border)] px-1">
          {isSignedIn && <FeaturebaseBugButton />}
          <MobileProblemDrawer
            currentProblemSlug={problemSlug}
            listId={listId}
          />
        </div>
      </div>

      {/* Tab panels */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {/* Description tab — stacked scroll layout */}
        <div
          id="tabpanel-description"
          role="tabpanel"
          aria-labelledby="tab-description"
          className={cn(
            'h-full overflow-x-hidden overflow-y-auto overscroll-contain',
            activeTab !== 'description' && 'hidden',
          )}
        >
          <div className="flex min-w-0 flex-col gap-2 p-2 pb-[env(safe-area-inset-bottom)]">
            <div className="pane-card overflow-x-hidden overflow-y-auto [&>div]:overflow-x-hidden [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_table]:max-w-full [&_table]:overflow-x-auto">
              <DescriptionPane />
            </div>

            <div
              className="pane-card overflow-hidden"
              style={{ height: '50vh', minHeight: '300px' }}
            >
              <CodeEditorPane />
            </div>

            {problemSlug && (
              <div className="pane-card overflow-x-hidden overflow-y-auto">
                <TestCasesPane problemSlug={problemSlug} />
              </div>
            )}

            <div
              ref={testResultsRef}
              className="pane-card overflow-x-hidden overflow-y-auto"
            >
              <TestResultsPane />
            </div>
          </div>
        </div>

        {/* Editorial tab */}
        <div
          id="tabpanel-editorial"
          role="tabpanel"
          aria-labelledby="tab-editorial"
          className={cn(
            'h-full overflow-y-auto overscroll-contain',
            activeTab !== 'editorial' && 'hidden',
          )}
        >
          <div className="pane-card h-full overflow-auto">
            <EditorialPane />
          </div>
        </div>

        {/* Notes tab */}
        <div
          id="tabpanel-notes"
          role="tabpanel"
          aria-labelledby="tab-notes"
          className={cn(
            'h-full overflow-y-auto overscroll-contain',
            activeTab !== 'notes' && 'hidden',
          )}
        >
          <div className="pane-card h-full overflow-auto">
            <NotesPane />
          </div>
        </div>

        {/* Attempts tab */}
        <div
          id="tabpanel-attempts"
          role="tabpanel"
          aria-labelledby="tab-attempts"
          className={cn(
            'h-full overflow-y-auto overscroll-contain',
            activeTab !== 'attempts' && 'hidden',
          )}
        >
          <div className="pane-card h-full overflow-auto">
            <AttemptsPane
              problemId={problemId ?? undefined}
              autoExpandExecutionId={autoExpandExecutionId}
              onSwitchToCode={() => setActiveTab('description')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
